import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import toast from "react-hot-toast";

export default function DashboardLayout({ navItems, children, sectionLabel }) {
  const { user, logout } = useAuth();
  const navigate         = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const styles = getStyles(isDark);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Signed out successfully.");
  };

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: collapsed ? "64px" : "240px" }}>
        {/* Logo */}
        <div style={styles.sidebarLogo}>
          <div style={styles.logoMark}>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none" style={{ display: "block" }}>
              <rect width="32" height="32" rx="8" fill="#2563EB"/>
              <path d="M8 16C8 11.58 11.58 8 16 8V12C13.79 12 12 13.79 12 16C12 18.21 13.79 20 16 20V24C11.58 24 8 20.42 8 16Z" fill="white"/>
              <path d="M16 8C20.42 8 24 11.58 24 16H20C20 13.79 18.21 12 16 12V8Z" fill="#93C5FD"/>
              <circle cx="20" cy="20" r="3" fill="#BFDBFE"/>
            </svg>
          </div>
          {!collapsed && (
            <div>
              <div style={styles.logoName}>EliyaQ</div>
              <div style={styles.logoRole}>{sectionLabel}</div>
            </div>
          )}
          <button
            onClick={() => setCollapsed(p => !p)}
            style={styles.collapseBtn}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? "›" : "‹"}
          </button>
        </div>

        {/* Nav */}
        <nav style={styles.nav}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              style={({ isActive }) => ({
                ...styles.navLink,
                ...(isActive ? styles.navLinkActive : {}),
              })}
              title={collapsed ? item.label : undefined}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Theme toggle + User info + logout */}
        <div style={styles.sidebarFooter}>
          <button
            onClick={toggleTheme}
            style={styles.themeToggleBtn}
            title={isDark ? "Switch to light theme" : "Switch to dark theme"}
          >
            <span>{isDark ? "☀️" : "🌙"}</span>
            {!collapsed && <span>{isDark ? "Light theme" : "Dark theme"}</span>}
          </button>

          {!collapsed && (
            <div style={styles.userBlock}>
              <div style={styles.userAvatar}>
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user?.name}</div>
                <div style={styles.userEmail}>{user?.email}</div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn} title="Sign out">
            <span>⎋</span>
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={styles.main}>
        {children}
      </main>
    </div>
  );
}

function getStyles(isDark) {
  return {
    shell: {
      display: "flex",
      minHeight: "100vh",
      background: isDark ? "#060D1F" : "#F3F7FF",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    },
    sidebar: {
      background: isDark ? "rgba(10,16,35,0.98)" : "#FFFFFF",
      borderRight: isDark ? "1px solid rgba(37,99,235,0.15)" : "1px solid #E2E8F0",
      display: "flex",
      flexDirection: "column",
      transition: "width 0.2s ease",
      flexShrink: 0,
      position: "sticky",
      top: 0,
      height: "100vh",
      overflow: "hidden",
    },
    sidebarLogo: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "20px 16px 16px",
      borderBottom: isDark ? "1px solid rgba(37,99,235,0.1)" : "1px solid #EEF2F7",
      position: "relative",
    },
    logoMark: { flexShrink: 0, lineHeight: 0 },
    logoImg: { width: "28px", height: "28px", borderRadius: "7px", objectFit: "cover", display: "block" },
    logoName: {
      fontSize: "16px",
      fontWeight: "700",
      color: isDark ? "#fff" : "#0F1E3D",
      letterSpacing: "-0.3px",
      lineHeight: "1.2",
    },
    logoRole: {
      fontSize: "10px",
      color: "#2563EB",
      fontWeight: "600",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    collapseBtn: {
      position: "absolute",
      right: "8px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "rgba(37,99,235,0.1)",
      border: "1px solid rgba(37,99,235,0.2)",
      borderRadius: "6px",
      color: "#2563EB",
      width: "22px",
      height: "22px",
      cursor: "pointer",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
    },
    nav: {
      flex: 1,
      padding: "12px 8px",
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      overflowY: "auto",
    },
    navLink: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "9px 10px",
      borderRadius: "8px",
      textDecoration: "none",
      color: isDark ? "#64748B" : "#64748B",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background 0.15s, color 0.15s",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    navLinkActive: {
      background: isDark ? "rgba(37,99,235,0.15)" : "rgba(37,99,235,0.08)",
      color: isDark ? "#60A5FA" : "#2563EB",
    },
    navIcon: {
      fontSize: "17px",
      flexShrink: 0,
      width: "20px",
      textAlign: "center",
    },
    navLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    sidebarFooter: {
      padding: "12px 8px",
      borderTop: isDark ? "1px solid rgba(37,99,235,0.1)" : "1px solid #EEF2F7",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    themeToggleBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: isDark ? "rgba(245,158,11,0.08)" : "rgba(37,99,235,0.06)",
      border: isDark ? "1px solid rgba(245,158,11,0.2)" : "1px solid rgba(37,99,235,0.12)",
      cursor: "pointer",
      color: isDark ? "#FBBF24" : "#2563EB",
      fontSize: "13px",
      fontWeight: "600",
      width: "100%",
    },
    userBlock: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: isDark ? "var(--input-bg)" : "#F1F5F9",
    },
    userAvatar: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: "linear-gradient(135deg, #2563EB, #7C3AED)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "13px",
      fontWeight: "700",
      color: "#fff",
      flexShrink: 0,
    },
    userInfo: { minWidth: 0 },
    userName: {
      fontSize: "13px",
      fontWeight: "600",
      color: isDark ? "#E2E8F0" : "#0F1E3D",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    userEmail: {
      fontSize: "11px",
      color: isDark ? "#475569" : "#94A3B8",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    logoutBtn: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 10px",
      borderRadius: "8px",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#475569" : "#94A3B8",
      fontSize: "13px",
      fontWeight: "500",
      width: "100%",
      transition: "background 0.15s, color 0.15s",
    },
    main: {
      flex: 1,
      overflow: "auto",
      minHeight: "100vh",
    },
  };
}
